import { isBeforeOpenDay, eventOpenDay } from '../../../../utils/openDayDate';
/**
 * Хук загрузки истории событий посадки, группировки и обработки CRUD-операций.
 */
import { useState, useMemo, useCallback } from 'react';
import { Form, message } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  useLazyGetPlantingEventsQuery,
  useUpdateEventMutation,
  useDeleteEventMutation,
} from '../../../../api/plantingEventsApi';
import { toastError } from '../../../../utils/toast';
import { TECHNICAL_PROPERTY_CODES } from '../constants';
import { resolveEventDate, resolveEditableEventDateField, toDayjsDateOnly } from '../utils';
import type { PlantingEvent } from '../../../../types/plantingEvents';
import { useSave } from '../../../../contexts/WorldContext';

interface PropertyHistoryRecord {
  date: string;
  value: any;
  unit?: string;
}

interface LocationHistoryRecord {
  date: string;
  location: string;
}

export interface PropertyHistory {
  measurements: Record<string, PropertyHistoryRecord[]>;
  locations: LocationHistoryRecord[];
  otherEvents: PlantingEvent[];
}

export function useEventHistory(
  plantingId: string | undefined,
  reloadPlantingDerivedData: () => Promise<void>,
) {
  const save = useSave();
  const { t } = useTranslation('plantings');
  const [editEventForm] = Form.useForm<{ eventDate: dayjs.Dayjs | null; notes: string }>();
  const [editingEvent, setEditingEvent] = useState<PlantingEvent | null>(null);
  const [editEventOpen, setEditEventOpen] = useState(false);
  const [savingEvent, setSavingEvent] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  const [
    fetchEvents,
    { data: eventsRaw, isLoading: loadingEvents, isFetching: fetchingEvents },
  ] = useLazyGetPlantingEventsQuery();
  const [updateEvent] = useUpdateEventMutation();
  const [deleteEvent] = useDeleteEventMutation();

  // Сортировка по дате (новые — сверху)
  const events = useMemo(() => {
    if (!eventsRaw) return [];
    return [...eventsRaw].sort((a, b) => {
      const dateA = a.actualDate || a.plannedDate || '';
      const dateB = b.actualDate || b.plannedDate || '';
      return dateB.localeCompare(dateA);
    });
  }, [eventsRaw]);

  // Группировка по типу: измерения / локации / прочие
  const propertyHistory = useMemo<PropertyHistory>(() => {
    if (!events) return { measurements: {}, locations: [], otherEvents: [] };

    const measurements: Record<string, PropertyHistoryRecord[]> = {};
    const locations: LocationHistoryRecord[] = [];
    const otherEvents: PlantingEvent[] = [];

    events.forEach((event) => {
      const eventDate = resolveEventDate(event) || '';
      let hasMeasurableProperties = false;

      if (event.properties && event.properties.length > 0) {
        event.properties.forEach((prop) => {
          if (prop.semanticType !== 'measured' || TECHNICAL_PROPERTY_CODES.has(prop.propertyCode)) return;
          hasMeasurableProperties = true;
          const propKey = prop.propertyName;
          if (!measurements[propKey]) measurements[propKey] = [];

          let value: any = null;
          if (prop.valueNumber !== null) value = prop.valueNumber;
          else if (prop.valueText !== null) value = prop.valueText;
          else if (prop.valueBoolean !== null) value = prop.valueBoolean ? t('common:actions.yes') : t('common:actions.no');

          if (value !== null) {
            measurements[propKey].push({ date: eventDate, value, unit: prop.unit || undefined });
          }
        });
      }

      if (event.kind === 'transplant' || event.kind === 'relocation') {
        const parts: string[] = [];
        if (event.fieldName) parts.push(t('location.fieldNamed', { name: event.fieldName }));
        if (event.plotName) parts.push(t('location.plotNamed', { name: event.plotName }));
        if (event.bedName) parts.push(t('location.bedNamed', { name: event.bedName }));
        if (parts.length > 0) locations.push({ date: eventDate, location: parts.join(' / ') });
      }

      if (!hasMeasurableProperties) otherEvents.push(event);
    });

    return { measurements, locations, otherEvents };
  }, [events, t]);

  // ── Handlers ──

  const handleLoadHistory = useCallback(async () => {
    if (!plantingId) return;
    try {
      await fetchEvents({ saveId: save.id, plantingId });
      setHistoryLoaded(true);
    } catch (err) {
      toastError(err);
    }
  }, [plantingId, fetchEvents, save.id]);

  const reloadHistoryIfNeeded = useCallback(async () => {
    if (!plantingId || !historyLoaded) return;
    await fetchEvents({ saveId: save.id, plantingId });
  }, [plantingId, historyLoaded, fetchEvents, save.id]);

  // Открытый день D+1: события датой раньше лежат в посчитанном прошлом —
  // дату и удаление сервер не принимает (EVENT_PAST_LOCKED), заметку — принимает.
  const openDay = eventOpenDay(save.cursor?.simDate || save.cursor?.sim_date);
  const isEventLocked = useCallback(
    (event: PlantingEvent) => isBeforeOpenDay(resolveEventDate(event), openDay),
    [openDay],
  );
  const editingEventLocked = editingEvent ? isEventLocked(editingEvent) : false;

  const handleOpenEditEvent = useCallback((event: PlantingEvent) => {
    const initialDateRaw = resolveEventDate(event);
    const fallbackDate = openDay;
    const initialDate = toDayjsDateOnly(initialDateRaw) || toDayjsDateOnly(fallbackDate);
    setEditingEvent(event);
    setEditEventOpen(true);
    editEventForm.setFieldsValue({
      eventDate: initialDate && initialDate.isValid() ? initialDate : null,
      notes: event.notes || '',
    });
  }, [editEventForm, openDay]);

  const handleSaveEventCorrection = useCallback(async () => {
    if (!editingEvent) return;
    try {
      const values = await editEventForm.validateFields();
      const eventDate = values.eventDate?.format('YYYY-MM-DD');
      const dateField = resolveEditableEventDateField(editingEvent);
      const locked = isEventLocked(editingEvent);
      setSavingEvent(true);
      await updateEvent({
        saveId: save.id,
        eventId: editingEvent.id,
        payload: locked
          ? { notes: values.notes?.trim() || '' }
          : { notes: values.notes?.trim() || '', [dateField]: eventDate },
      }).unwrap();
      await reloadHistoryIfNeeded();
      await reloadPlantingDerivedData();
      setEditEventOpen(false);
      setEditingEvent(null);
      message.success(t('history.eventCorrected'));
    } catch (err) {
      toastError(err);
    } finally {
      setSavingEvent(false);
    }
  }, [editingEvent, editEventForm, isEventLocked, updateEvent, reloadHistoryIfNeeded, reloadPlantingDerivedData, save.id, t]);

  const handleDeleteEvent = useCallback(async (eventId: string) => {
    try {
      await deleteEvent({ saveId: save.id, eventId, plantingId }).unwrap();
      await reloadHistoryIfNeeded();
      await reloadPlantingDerivedData();
      message.success(t('history.eventDeleted'));
    } catch (err) {
      toastError(err);
    }
  }, [deleteEvent, reloadHistoryIfNeeded, reloadPlantingDerivedData, save.id, plantingId, t]);

  const closeEditModal = useCallback(() => {
    setEditEventOpen(false);
    setEditingEvent(null);
  }, []);

  return {
    events,
    propertyHistory,
    historyLoaded,
    loadingEvents,
    fetchingEvents,
    editEventForm,
    editingEvent,
    editEventOpen,
    savingEvent,
    handleLoadHistory,
    handleOpenEditEvent,
    handleSaveEventCorrection,
    handleDeleteEvent,
    closeEditModal,
    reloadHistoryIfNeeded,
    openDay,
    isEventLocked,
    editingEventLocked,
  };
}
